import type { CreateRestaurantInquiryItem, CreateRestaurantInquiryPayload } from 'apis/restaurant-inquiries';
import { PaymentOption, VolumePeriod } from 'apis/restaurant-inquiries';
import type { SalesLookupVariant } from 'apis/sales-lookup';
import { resolveTier, tierUnit } from 'utils/price-units';
import { composeItemNotes } from './compose-item-notes';
import type { HeadlinePrice } from './headline';

// The five "open to switch" dimensions of an inquiry item, mirroring the CMS
// item wizard (brand / origin / state / spec / packaging). All default to true,
// which is also the backend default for items created without them.
export interface FlexibilityFlags {
  brandFlexible: boolean;
  originFlexible: boolean;
  stateFlexible: boolean;
  specFlexible: boolean;
  packagingFlexible: boolean;
}

export const FLEXIBILITY_KEYS: (keyof FlexibilityFlags)[] = [
  'brandFlexible',
  'originFlexible',
  'stateFlexible',
  'specFlexible',
  'packagingFlexible',
];

export interface LogPriceFormValues extends FlexibilityFlags {
  reason: { value: string; label: string } | null;
  // Supplier options come from the suppliers search: value = supplier id,
  // LABEL = the name (which is what the notes line needs).
  currentSupplier: { value: number; label: string } | null;
  // Brand options carry the brand name as VALUE — the synthesized Add-option's
  // label reads 'Add "x"' and must never leak into the notes.
  brand: { value: string; label: string } | null;
  // Both optional since 2026-09-10 — '' means not captured. When non-empty the
  // form validates them as positive numbers before this module sees them.
  price: string;
  volume: string;
  notes: string;
}

export function defaultLogPriceFormValues(variant: Pick<SalesLookupVariant, 'brand'>): LogPriceFormValues {
  return {
    reason: null,
    currentSupplier: null,
    brand: variant.brand ? { value: variant.brand, label: variant.brand } : null,
    price: '',
    volume: '',
    notes: '',
    brandFlexible: true,
    originFlexible: true,
    stateFlexible: true,
    specFlexible: true,
    packagingFlexible: true,
  };
}

// '' / whitespace → undefined (not captured). Anything else parses as a number;
// the form has already rejected non-numeric or non-positive input.
export function parseOptionalNumber(raw: string): number | undefined {
  if (!raw || !raw.trim()) return undefined;
  const value = parseFloat(raw);
  return isNaN(value) ? undefined : value;
}

// Form values → one inquiry line item. Currency is omitted (backend defaults
// AED); the competitor price goes into currentPrice exactly as typed; volume
// and price are simply absent when the rep didn't capture them.
export function buildLoggedItem(args: {
  variant: SalesLookupVariant;
  headline: HeadlinePrice | null;
  values: LogPriceFormValues;
}): CreateRestaurantInquiryItem {
  const { variant, headline, values } = args;

  const fallbackTier = resolveTier(variant);
  const priceUnit = headline?.unit ?? (fallbackTier ? tierUnit(variant, fallbackTier) : null) ?? variant.baseUnit;

  const hasReference = headline != null && headline.price > 0;
  const currentSupplier = values.currentSupplier?.label.trim() ?? '';
  const brand = values.brand?.value.trim() ?? '';
  const price = parseOptionalNumber(values.price);
  const volume = parseOptionalNumber(values.volume);

  const notes = composeItemNotes({
    note: values.notes,
    price,
    priceUnit,
    currentSupplier: currentSupplier || undefined,
    brand: brand || undefined,
    reasonLabel: values.reason?.label,
    ourPrice: hasReference ? headline.price : undefined,
    ourPriceKind: hasReference ? headline.kind : undefined,
    ourPriceUnit: hasReference ? headline.unit : undefined,
    ourPriceSupplier: hasReference ? headline.supplierName : undefined,
  });

  return {
    masterProductId: variant.id,
    ...(volume !== undefined ? { volume } : {}),
    // Reps report a per-order quantity in the meeting (stakeholder decision
    // 2026-07-31 — was WEEKLY in the first cut).
    volumePeriod: VolumePeriod.PER_ORDER,
    ...(price !== undefined ? { currentPrice: price } : {}),
    brandFlexible: values.brandFlexible,
    originFlexible: values.originFlexible,
    stateFlexible: values.stateFlexible,
    specFlexible: values.specFlexible,
    packagingFlexible: values.packagingFlexible,
    ...(notes ? { notes } : {}),
  };
}

export function buildCreateInquiryPayload(
  restaurantId: number,
  items: CreateRestaurantInquiryItem[]
): CreateRestaurantInquiryPayload {
  return {
    restaurantId,
    paymentOption: PaymentOption.CASH,
    notes: 'Created from Sales Lookup',
    items,
  };
}

// List ordering isn't contractual — pick max id from the returned page.
export function pickNewestInquiry<T extends { id: number }>(rows: T[]): T | null {
  return rows.reduce<T | null>((newest, row) => (!newest || row.id > newest.id ? row : newest), null);
}
